import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Upload, FileText, X } from "lucide-react";
import Reveal from "@/components/Reveal";
import Aperture from "@/components/Aperture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  "Freelance Photographer",
  "Photo Editor",
  "Production Assistant",
  "Sales & Partnerships Coordinator",
  "Other",
];

const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "application/rtf",
];
const MAX_BYTES = 8 * 1024 * 1024;

const initial = {
  full_name: "",
  email: "",
  phone: "",
  availability: "",
  birthdate: "",
  position: "",
};

const Careers = () => {
  const [form, setForm] = useState(initial);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (ALLOWED_TYPES.length && !ALLOWED_TYPES.includes(f.type) && !/\.(pdf|doc|docx|txt|rtf)$/i.test(f.name)) {
      toast.error("Unsupported file. Use PDF, DOC, DOCX, RTF, or TXT.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Resume too large. Max 8 MB.");
      e.target.value = "";
      return;
    }
    setResume(f);
  };

  const clearFile = () => {
    setResume(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.position) {
      toast.error("Full name, email, and position are required.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      await axios.post(`${API}/careers`, fd);
      setSent(true);
      toast.success("Application received.", {
        description: "We review every submission personally — expect a reply if there’s a fit.",
      });
      setForm(initial);
      clearFile();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't submit right now.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative pt-40 pb-12 md:pt-52 overflow-hidden border-b border-white/5" data-testid="careers-hero">
        <div className="absolute -right-40 -top-20 opacity-40 hidden md:block">
          <Aperture size={520} />
        </div>
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">
              <span className="text-white/35 mr-3">JOIN / 00</span>Careers
            </div>
            <h1 className="font-display text-6xl md:text-[8vw] leading-[0.92] tracking-[-0.03em] text-white max-w-5xl">
              Build with <span className="font-serif-display text-[var(--gold)]">Lucra</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-white/65 text-lg leading-relaxed">
              We are quietly building a small, deliberate team across Atlanta and Washington, D.C. If you treat the
              craft seriously — and the people in front of the lens even more seriously — we’d love to meet you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Open roles preview */}
      <section className="py-16 md:py-24 border-b border-white/5">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-8">
            <span className="text-white/35 mr-3">JOIN / 01</span>Open Roles
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5">
            {POSITIONS.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div className="bg-[#0a0a0a] p-6 h-full beam-card" data-testid={`role-${i}`}>
                  <div className="font-mono-tech text-xs text-[var(--cyan)] mb-3">0{i + 1}</div>
                  <div className="font-display text-xl text-white leading-tight">{p}</div>
                  <div className="mt-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/40">
                    ATL · DC · Remote
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-4">
              <span className="text-white/35 mr-3">JOIN / 02</span>Apply
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
              Tell us about <span className="font-serif-display text-[var(--gold)]">yourself</span>.
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Every application is read personally. If we don’t have an opening today, we keep strong applicants in
              a private bench file for future projects.
            </p>
          </Reveal>

          <form onSubmit={submit} className="md:col-span-8 space-y-10" data-testid="careers-form">
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              <div>
                <label className="lucra-label">Full Name *</label>
                <input className="lucra-input mt-2" value={form.full_name} onChange={update("full_name")} required data-testid="careers-input-name" />
              </div>
              <div>
                <label className="lucra-label">Email *</label>
                <input type="email" className="lucra-input mt-2" value={form.email} onChange={update("email")} required data-testid="careers-input-email" />
              </div>
              <div>
                <label className="lucra-label">Phone</label>
                <input className="lucra-input mt-2" value={form.phone} onChange={update("phone")} data-testid="careers-input-phone" />
              </div>
              <div>
                <label className="lucra-label">Availability</label>
                <input className="lucra-input mt-2" placeholder="Weekends · Full-time · Nights" value={form.availability} onChange={update("availability")} data-testid="careers-input-availability" />
              </div>
              <div>
                <label className="lucra-label">Birthdate</label>
                <input type="date" className="lucra-input mt-2" value={form.birthdate} onChange={update("birthdate")} data-testid="careers-input-birthdate" />
              </div>
              <div>
                <label className="lucra-label">Position *</label>
                <select className="lucra-input mt-2 bg-black" value={form.position} onChange={update("position")} required data-testid="careers-input-position">
                  <option value="">Select…</option>
                  {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="lucra-label">Resume</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.rtf,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/plain"
                  onChange={onFile}
                  className="hidden"
                  data-testid="careers-input-resume"
                />
                {!resume ? (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="mt-3 w-full flex items-center justify-between px-5 py-5 border border-dashed border-white/15 hover:border-[var(--gold)]/60 hover:bg-[var(--gold)]/5 transition-colors text-left group"
                    data-testid="careers-upload-trigger"
                  >
                    <div className="flex items-center gap-3 text-white/65 group-hover:text-white">
                      <Upload size={18} />
                      <span className="font-mono-tech text-xs tracking-[0.18em] uppercase">Upload your resume</span>
                    </div>
                    <span className="font-mono-tech text-[10px] text-white/35">PDF · DOC · DOCX · 8MB max</span>
                  </button>
                ) : (
                  <div className="mt-3 flex items-center justify-between px-5 py-4 border border-[var(--gold)]/40 bg-[var(--gold)]/5">
                    <div className="flex items-center gap-3 text-white" data-testid="careers-resume-name">
                      <FileText size={18} className="text-[var(--gold)]" />
                      <div>
                        <div className="text-sm">{resume.name}</div>
                        <div className="font-mono-tech text-[10px] text-white/40 mt-0.5">{(resume.size / 1024).toFixed(0)} KB</div>
                      </div>
                    </div>
                    <button type="button" onClick={clearFile} className="text-white/60 hover:text-white" aria-label="Remove file" data-testid="careers-resume-remove">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-gold" data-testid="careers-submit">
              <span>{loading ? "Sending…" : sent ? "Application Sent ✓" : "Submit Application"}</span>
              <ArrowUpRight size={14} />
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Careers;
